import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, useTheme, Divider } from '@mui/material';
import ABRErrorCard from './ABRErrorCard';
import CDNErrorCard from './CDNErrorCard';
import OriginErrorCard from './OriginErrorCard';
import DVQAErrorCard from './DVQAErrorCard';
import HeaderCheckErrorCard from './HeaderCheckErrorCard';
import StalledErrorCard from './StalledErrorCard';
import EmptyManifestCard from './EmptyManifestCard';
import BlackScreenCard from './BlackScreenCard';
import FormatErrorCard from './FormatErrorCard';
import CDNSpecificErrorCard from './CDNSpecificErrorCard';
import E2EMetricsCard from './E2EMetricsCard';
import { getErrorStates } from '../../api/client';

function ErrorSimulationCards({ pipeline }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [errorStates, setErrorStates] = useState(null);
  const [formatErrors, setFormatErrors] = useState({});
  const [cdnSpecificErrors, setCdnSpecificErrors] = useState({});
  const [availableFormats, setAvailableFormats] = useState([]);
  const [availableCdns, setAvailableCdns] = useState([]);
  const [loading, setLoading] = useState(false);

  // Only render if pipeline has touchstream_config_id
  if (!pipeline.touchstream_config_id) {
    return null;
  }

  const fetchErrorStates = async () => {
    try {
      setLoading(true);
      const states = await getErrorStates(pipeline.id);
      setErrorStates(states.errors);
      setFormatErrors(states.format_errors || {});
      setCdnSpecificErrors(states.cdn_specific_errors || {});

      // Extract available formats and CDNs from the response
      const formats = Object.values(states.format_errors || {}).map(e => e.format);
      const cdns = Object.values(states.cdn_specific_errors || {}).map(e => e.cdn);
      setAvailableFormats(formats);
      setAvailableCdns(cdns);
    } catch (error) {
      console.error('Failed to fetch error states:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pipeline.touchstream_config_id) {
      fetchErrorStates();
    }
  }, [pipeline.id, pipeline.touchstream_config_id]);

  const handleErrorToggle = async () => {
    // Refresh error states after any toggle
    await fetchErrorStates();
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        background: isDark
          ? 'rgba(255, 100, 100, 0.05)'
          : 'rgba(255, 0, 0, 0.02)',
        border: isDark
          ? '1px solid rgba(255, 100, 100, 0.2)'
          : '1px solid rgba(255, 0, 0, 0.1)',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          color: isDark ? '#ff6b6b' : '#d32f2f',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        Error Simulation
      </Typography>

      <Grid container spacing={1}>
        {/* Available error simulations */}
        <Grid item xs={6} sm={4}>
          <ABRErrorCard
            pipeline={pipeline}
            isActive={errorStates?.abr?.active || false}
            onToggle={handleErrorToggle}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <CDNErrorCard
            pipeline={pipeline}
            isActive={errorStates?.cdn_error?.active || false}
            onToggle={handleErrorToggle}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <OriginErrorCard
            pipeline={pipeline}
            isActive={errorStates?.origin_error?.active || false}
            onToggle={handleErrorToggle}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <HeaderCheckErrorCard
            pipeline={pipeline}
            isActive={errorStates?.header_check?.active || false}
            onToggle={handleErrorToggle}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <BlackScreenCard
            pipeline={pipeline}
            isActive={errorStates?.black_screen?.active || false}
            onToggle={handleErrorToggle}
          />
        </Grid>
        {/* Coming soon error simulations */}
        <Grid item xs={6} sm={4}>
          <DVQAErrorCard
            pipeline={pipeline}
            isActive={errorStates?.dvqa?.active || false}
            onToggle={handleErrorToggle}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StalledErrorCard
            pipeline={pipeline}
            isActive={errorStates?.stalled?.active || false}
            onToggle={handleErrorToggle}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <EmptyManifestCard
            pipeline={pipeline}
            isActive={errorStates?.empty_manifest?.active || false}
            onToggle={handleErrorToggle}
          />
        </Grid>
      </Grid>

      {/* Format-Based Errors Section - only show if formats exist */}
      {availableFormats.length > 0 && (
        <>
          <Divider sx={{ my: 2, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
          <Typography
            variant="caption"
            sx={{
              mb: 1,
              display: 'block',
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              fontWeight: 500,
            }}
          >
            Format-Based Errors
          </Typography>
          <Grid container spacing={1}>
            {availableFormats.map((format) => (
              <Grid item xs={6} sm={4} key={`format-${format}`}>
                <FormatErrorCard
                  pipeline={pipeline}
                  format={format}
                  isActive={formatErrors[`format_${format.toLowerCase()}`]?.active || false}
                  onToggle={handleErrorToggle}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* CDN-Specific Errors Section - only show if CDNs exist */}
      {availableCdns.length > 0 && (
        <>
          <Divider sx={{ my: 2, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
          <Typography
            variant="caption"
            sx={{
              mb: 1,
              display: 'block',
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              fontWeight: 500,
            }}
          >
            CDN-Specific Errors
          </Typography>
          <Grid container spacing={1}>
            {availableCdns.map((cdn) => (
              <Grid item xs={6} sm={4} key={`cdn-${cdn}`}>
                <CDNSpecificErrorCard
                  pipeline={pipeline}
                  cdnName={cdn}
                  isActive={cdnSpecificErrors[`cdn_${cdn.toLowerCase()}`]?.active || false}
                  onToggle={handleErrorToggle}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* E2E Metrics Section - only show if E2E is configured */}
      {pipeline.e2e_channel_key && (
        <>
          <Divider sx={{ my: 2, borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
          <Typography
            variant="caption"
            sx={{
              mb: 1,
              display: 'block',
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              fontWeight: 500,
            }}
          >
            Touchstream E2E Metrics
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <E2EMetricsCard
                pipeline={pipeline}
                onToggle={handleErrorToggle}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

export default ErrorSimulationCards;
